import { useEffect, useMemo, useState } from 'react';
import clsx, { ClassValue } from 'clsx';
import { FileUrlBuilderOptions, buildFileUrl } from '@sanity/asset-utils';
import { AspectRatio, ImageSourceSizes, useMediaQuery, type AspectRatioProps } from '@ag1/ui';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { ResponsiveMedia, SanityReference } from 'types/studio/studio';
import { useHasVideoBeenPlayed } from './useHasVideoBeenPlayed';
import styles from './StudioResponsiveMedia.module.scss';

type Props = {
  className?: string;
  childClassName?: ClassValue;
  eagerLoading?: boolean;
  sizes?: ImageSourceSizes;

  // Video control props for controlling the video player from a higher level component
  videoRef?: React.RefObject<HTMLVideoElement | null>;

  /**
   * This will be placed within the .video element, which has position: relative.
   * This overlay can then be positioned absolutely within the .video element.
   */
  customVideoOverlay?: React.ReactNode;
  autoPlay?: boolean;
  muted?: boolean;
  onVideoMounted?: () => void;
} & ResponsiveMedia &
  Pick<AspectRatioProps, 'aspectRatio'> & {
    // This addition accomodates custom content from Atlas.
    video?: {
      asset?: SanityReference<any>;
    };
    tabletVideo?: {
      asset?: SanityReference<any>;
    };
    mobileVideo?: {
      asset?: SanityReference<any>;
    };
  };

const getFormattedVideo = (baseVideo?: Props['video']) => {
  let url: string | null = null;
  if (baseVideo?.video?.asset) {
    const asset: FileUrlBuilderOptions = {
      // eslint-disable-next-line no-underscore-dangle
      assetId: baseVideo.asset?._ref || baseVideo.video.asset?._ref || '',
      projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || '',
      dataset: process.env.NEXT_PUBLIC_SANITY_DATASET || '',
      extension: 'mp4'
    };

    url = buildFileUrl(asset).replace('/file-', '/').replace('-mp4', '');
  }

  return {
    ...baseVideo,
    video: {
      ...baseVideo?.video,
      asset: {
        ...baseVideo?.video?.asset,
        ...baseVideo?.asset
      }
    },
    url
  };
};

const StudioResponsiveMedia = ({
  mediaType,
  video,
  mobileVideo,
  tabletVideo,
  image,
  mobileImage,
  tabletImage,
  withMobileMedia,
  withTabletMedia,
  aspectRatio = 1,
  className,
  mediaTypeForMobile,
  mediaTypeForTablet,
  childClassName: childClassNameProp,
  eagerLoading = false,
  sizes,
  showImageAsVideoThumbnail = false,
  videoRef,
  autoPlay = true,
  muted = true,
  customVideoOverlay,
  onVideoMounted
}: Props) => {
  const aboveSmall = useMediaQuery('s');
  const aboveMedium = useMediaQuery('m');

  const hasTabletVideo =
    (withTabletMedia && mediaTypeForTablet === 'tabletVideo') ||
    (!withTabletMedia && mediaType === 'video');

  const hasMobileVideo =
    (withMobileMedia && mediaTypeForMobile === 'mobileVideo') ||
    (!withMobileMedia && mediaType === 'video');

  const shouldShowVideo =
    !withTabletMedia || !withMobileMedia
      ? mediaType === 'video'
      : mediaType === 'video' || hasTabletVideo || hasMobileVideo;

  // Force the position to "top" so it matches the positioning of the video.
  const alternativeImage = useMemo(
    () =>
      video?.alternativeImage
        ? ({ ...video?.alternativeImage, position: 'top' } as const)
        : undefined,
    [video?.alternativeImage]
  );

  const alternativeTabletImage = useMemo(
    () =>
      hasTabletVideo && tabletVideo?.alternativeImage
        ? ({ ...tabletVideo?.alternativeImage, position: 'top' } as const)
        : undefined,
    [hasTabletVideo, tabletVideo?.alternativeImage]
  );

  const alternativeMobileImage = useMemo(
    () =>
      hasMobileVideo && mobileVideo?.alternativeImage
        ? ({ ...mobileVideo?.alternativeImage, position: 'top' } as const)
        : undefined,
    [hasMobileVideo, mobileVideo?.alternativeImage]
  );

  const formattedVideo = useMemo(() => {
    // Mobile (use tablet or desktop as fallback)
    if (!aboveSmall && hasMobileVideo) {
      return getFormattedVideo(mobileVideo ?? tabletVideo ?? video);
    }

    // Tablet (use desktop as fallback)
    if (!aboveMedium && hasTabletVideo) {
      return getFormattedVideo(tabletVideo ?? video);
    }

    // Desktop / default
    return getFormattedVideo(video);
  }, [aboveSmall, hasMobileVideo, aboveMedium, hasTabletVideo, mobileVideo, tabletVideo, video]);

  // We don't want to load the video until the component is mounted
  // (client-only). This helps with slower connections that would load the video
  // from the SSR HTML at the same time as JS and CSS required for the page.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (mounted && shouldShowVideo && onVideoMounted) {
      onVideoMounted();
    }
  }, [mounted, shouldShowVideo, onVideoMounted]);

  const hasVideoBeenPlayed = useHasVideoBeenPlayed({ videoRef, mounted, autoPlay });

  return (
    <AspectRatio
      aspectRatio={aspectRatio}
      fit={image?.fit || formattedVideo?.fit}
      position={image?.position || formattedVideo?.position}
      className={className}>
      {({ style, childClassName, containerClassName }) => {
        const AlternativeImage = alternativeImage ? (
          <StudioBaseImageV2
            image={alternativeImage}
            tabletImage={alternativeTabletImage}
            mobileImage={alternativeMobileImage}
            className={clsx(styles.alternativeImage)}
            eagerLoading={eagerLoading}
            sizes={sizes}
          />
        ) : null;
        const showImageAboveVideo = showImageAsVideoThumbnail && !hasVideoBeenPlayed && !autoPlay;

        return (
          <div className={containerClassName} style={style}>
            {shouldShowVideo && (
              <div
                className={clsx(
                  styles.videoWrapper,
                  childClassName,
                  childClassNameProp,
                  styles.showDesktop,
                  hasTabletVideo && styles.showTablet,
                  hasMobileVideo && styles.showMobile
                )}>
                {/* In order to show a fallback image while loading the video, we require at least a desktop image. */}
                {alternativeImage && !showImageAboveVideo && AlternativeImage}
                {/* Leave this after the image so when it loads it shows on top. */}
                {mounted && (
                  <>
                    {customVideoOverlay}
                    <video
                      ref={videoRef}
                      key={formattedVideo?.url || ''}
                      className={styles.video}
                      autoPlay={autoPlay}
                      loop
                      muted={muted}
                      playsInline>
                      {formattedVideo?.url && <source src={formattedVideo.url} type="video/mp4" />}
                      {/* Put here to resolve jsx-a11y/media-has-caption warning */}
                      {/* TODO: look into if this affects anything */}
                      <track kind="captions" />
                    </video>

                    {/* Show image as video thumbnail overlay when specified */}
                    {alternativeImage && showImageAboveVideo && AlternativeImage}
                  </>
                )}
              </div>
            )}

            {!shouldShowVideo && image && (
              <div
                className={clsx(
                  styles.imageWrapper,
                  styles.showDesktop,
                  !withTabletMedia && styles.showTablet,
                  !withMobileMedia && styles.showMobile
                )}>
                <StudioBaseImageV2
                  image={image}
                  className={clsx(childClassName, childClassNameProp)}
                  eagerLoading={eagerLoading}
                  sizes={sizes}
                />
              </div>
            )}

            {withTabletMedia && tabletImage && (
              <div className={clsx(styles.imageWrapper, styles.showTablet)}>
                <StudioBaseImageV2
                  image={tabletImage}
                  className={clsx(childClassName, childClassNameProp)}
                  eagerLoading={eagerLoading}
                  sizes={sizes}
                />
              </div>
            )}

            {withMobileMedia && mobileImage && (
              <div className={clsx(styles.imageWrapper, styles.showMobile)}>
                <StudioBaseImageV2
                  image={mobileImage}
                  className={clsx(childClassName, childClassNameProp)}
                  eagerLoading={eagerLoading}
                  sizes={sizes}
                />
              </div>
            )}
          </div>
        );
      }}
    </AspectRatio>
  );
};

export default StudioResponsiveMedia;
